import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useAccommodationUser } from './accommodationUser';
import {
  listStudentAccommodationChangeHistory,
  updateAccommodationSpecialStatus,
  publishAccommodationChange,
  addStudentAccommodationDisclosure,
  addStudentAccommodationReview,
  getAdultResidentialExtension,
  getStudentAccommodation,
  listAccommodationStaff,
  listStudentAccommodationDisclosures,
  listStudentAccommodationReviews,
  saveAdultResidentialExtension,
  saveStudentAccommodation,
  updateStudentAccommodationReviewCorrectiveAction,
} from './studentAccommodationsApi';

const PLAN_TYPE_OPTIONS = [
  ['IEP', 'IEP'],
  ['504', '504'],
  ['Other AFN', 'Other AFN'],
];

const NEED_OPTIONS = [
  ['mobility', 'Mobility / wheelchair / transfer assistance'],
  ['communication', 'Communication / AAC / limited speech'],
  ['hearing_visual', 'Hearing / visual notification needs'],
  ['sensory_behavioral', 'Sensory / autism / behavioral support'],
  ['medical_device', 'Medical / medication / device support'],
  ['supervision', 'Supervision / cognitive / elopement risk'],
  ['transportation', 'Transportation assistance'],
  ['other', 'Other'],
];

const CMIST_OPTIONS = [
  ['communication', 'Communication'],
  ['maintainingHealth', 'Maintaining Health'],
  ['independence', 'Independence'],
  ['supportSafety', 'Support, Safety & Self-Determination'],
  ['transportation', 'Transportation'],
];

const COMMUNICATION_CHECKS = [
  ['visualAlertRequired', 'Visual alert required'],
  ['audibleAlertRequired', 'Audible alert required'],
  ['tactileAlertRequired', 'Tactile alert required'],
  ['aslInterpreter', 'ASL / interpreter support'],
  ['aacDevice', 'AAC device'],
  ['pictureBoard', 'Picture board'],
  ['plainLanguage', 'Plain-language / simplified directions'],
];

const ADL_OPTIONS = [
  ['feeding', 'Feeding'],
  ['toileting', 'Toileting'],
  ['dressing', 'Dressing'],
  ['positioning', 'Positioning'],
  ['transfers', 'Transfers'],
  ['personalCare', 'Personal care'],
];

function clean(value) {
  return String(value ?? '').trim();
}

// Match completionMissingFields() in studentAccommodationsService.js.
// These requirements apply to marking Complete; they do not block Save Draft.
const PLAN_COMPLETION_REQUIREMENTS = [
  ['planTypes', 'Plan type'],
  ['accessNeedsOrInstructions', 'Access/functional need or specific instructions'],
  ['primaryLocation', 'Primary location'],
  ['refugeSite', 'Pre-evacuation / refuge site'],
  ['primaryRoute', 'Primary evacuation route'],
  ['secondaryRoute', 'Secondary evacuation route'],
  ['primaryStaffUserID', 'Primary staff assistant'],
  ['backupStaffUserID', 'Backup staff assistant'],
  ['evacuationFireInstructions', 'Evacuation / fire instructions'],
  ['lockdownSecureShelterInstructions', 'Lockdown / secure / shelter instructions'],
  ['relocationTransportationInstructions', 'Relocation / transportation instructions'],
  ['reunificationAccommodation', 'Reunification accommodation'],
  ['communicationMethod', 'Communication method'],
];

function missingPlanRequirements(plan) {
  return PLAN_COMPLETION_REQUIREMENTS.filter(([field]) => {
    if (field === 'planTypes') return !plan.planTypes.some((value) => clean(value));
    if (field === 'accessNeedsOrInstructions') {
      return !plan.accessNeeds.some((value) => clean(value)) && !clean(plan.specificInstructions);
    }
    return !clean(plan[field]);
  });
}

function errorText(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

function localDateInput(value = new Date()) {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function localDateTimeInput(value = new Date()) {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function blankCmistNeeds() {
  return {
    communication: false,
    maintainingHealth: false,
    independence: false,
    supportSafety: false,
    transportation: false,
  };
}

function blankEmergencyCommunicationNeeds() {
  return {
    preferredMethod: '',
    preferredLanguage: '',
    visualAlertRequired: false,
    audibleAlertRequired: false,
    tactileAlertRequired: false,
    aslInterpreter: false,
    aacDevice: false,
    pictureBoard: false,
    plainLanguage: false,
    other: '',
  };
}

function blankPowerDependentEquipment() {
  return {
    required: false,
    equipmentDescription: '',
    backupPowerRequired: false,
    batteryRuntimeMinutes: '',
  };
}

function blankDailyLocation() {
  return {
    locationName: '',
    scheduleContext: '',
    refugeSite: '',
    primaryRoute: '',
    secondaryRoute: '',
  };
}

function blankPlan() {
  return {
    planStatus: 'draft',
    planTypes: [],
    accessNeeds: [],
    specificInstructions: '',
    cmistNeeds: blankCmistNeeds(),
    emergencyCommunicationNeeds: blankEmergencyCommunicationNeeds(),
    powerDependentEquipment: blankPowerDependentEquipment(),
    serviceAnimalRequired: false,
    serviceAnimalInstructions: '',
    primaryLocation: '',
    refugeSite: '',
    primaryRoute: '',
    secondaryRoute: '',
    dailyLocations: [],
    primaryStaffUserID: '',
    backupStaffUserID: '',
    primaryStaffAcknowledgedAt: '',
    backupStaffAcknowledgedAt: '',
    equipmentGoKitLocation: '',
    medicationDevicePlan: '',
    evacuationFireInstructions: '',
    lockdownSecureShelterInstructions: '',
    relocationTransportationInstructions: '',
    reunificationAccommodation: '',
    accessibleTransportNeeded: false,
    staffRemainingWithStudent: '',
    criticalEquipmentMedication: '',
    parentGuardianConsideration: '',
    communicationMethod: '',
    lastDrillTested: '',
    planWorkedAsIntended: null,
    changesNeeded: '',
    nextReviewDate: '',
  };
}

function blankReview() {
  return {
    eventDate: localDateInput(),
    eventType: 'drill',
    workedAsIntended: '',
    problemObserved: '',
    correctiveAction: '',
    correctiveActionOwnerUserID: '',
    correctiveActionDueDate: '',
    correctiveActionCompletedAt: '',
    correctiveActionResolution: '',
    nextReviewDate: '',
  };
}

function blankAdultExtension() {
  return {
    canSelfEvacuate: null,
    evacuationAssistanceLevel: '',
    transferInstructions: '',
    stairCapability: '',
    mobilityDevice: '',
    emergencyADLNeeds: {
      feeding: false,
      toileting: false,
      dressing: false,
      positioning: false,
      transfers: false,
      personalCare: false,
    },
    oxygenRequired: false,
    refrigeratedMedicationRequired: false,
    essentialPersonalItems: '',
    supervisionLevel: '',
    elopementRisk: false,
    supervisionInstructions: '',
    emergencyContacts: [],
    staffMustAccompany: false,
    equipmentMustAccompany: false,
    medicationsMustAccompany: false,
    recordsMustAccompany: false,
  };
}

function blankEmergencyContact() {
  return { relationship: '', name: '', phone: '', email: '', instructions: '' };
}

function blankDisclosure() {
  return {
    disclosedAt: localDateTimeInput(),
    significantThreat: '',
    recipientParties: '',
    disclosureBasis: 'FERPA health/safety emergency exception',
    fieldsDisclosed: '',
    incidentReference: '',
  };
}

function normalizePlan(plan) {
  const base = blankPlan();
  if (!plan) return base;
  return {
    ...base,
    ...plan,
    planTypes: Array.isArray(plan.planTypes) ? plan.planTypes : [],
    accessNeeds: Array.isArray(plan.accessNeeds) ? plan.accessNeeds : [],
    cmistNeeds: { ...base.cmistNeeds, ...(plan.cmistNeeds || {}) },
    emergencyCommunicationNeeds: {
      ...base.emergencyCommunicationNeeds,
      ...(plan.emergencyCommunicationNeeds || {}),
    },
    powerDependentEquipment: {
      ...base.powerDependentEquipment,
      ...(plan.powerDependentEquipment || {}),
    },
    dailyLocations: Array.isArray(plan.dailyLocations) ? plan.dailyLocations : [],
    serviceAnimalRequired: Boolean(plan.serviceAnimalRequired),
    accessibleTransportNeeded: Boolean(plan.accessibleTransportNeeded),
    planWorkedAsIntended:
      plan.planWorkedAsIntended === true
        ? true
        : plan.planWorkedAsIntended === false
          ? false
          : null,
  };
}

function normalizeAdultExtension(extension) {
  const base = blankAdultExtension();
  if (!extension) return base;
  return {
    ...base,
    ...extension,
    emergencyADLNeeds: { ...base.emergencyADLNeeds, ...(extension.emergencyADLNeeds || {}) },
    emergencyContacts: Array.isArray(extension.emergencyContacts) ? extension.emergencyContacts : [],
    canSelfEvacuate:
      extension.canSelfEvacuate === true
        ? true
        : extension.canSelfEvacuate === false
          ? false
          : null,
    oxygenRequired: Boolean(extension.oxygenRequired),
    refrigeratedMedicationRequired: Boolean(extension.refrigeratedMedicationRequired),
    elopementRisk: Boolean(extension.elopementRisk),
    staffMustAccompany: Boolean(extension.staffMustAccompany),
    equipmentMustAccompany: Boolean(extension.equipmentMustAccompany),
    medicationsMustAccompany: Boolean(extension.medicationsMustAccompany),
    recordsMustAccompany: Boolean(extension.recordsMustAccompany),
  };
}

function displayStudentName(student) {
  return [student?.firstName, student?.lastName].filter(Boolean).join(' ') || `Student #${student?.studentId || ''}`;
}

function toggleArrayValue(values, value, checked) {
  const next = new Set(Array.isArray(values) ? values : []);
  if (checked) next.add(value);
  else next.delete(value);
  return Array.from(next);
}

export default function SpecialStudentModal(props) {
  const { user } = useAccommodationUser(props.userOverride);
  // Preserve the open record until the user resolves unsaved work, even when
  // its parent changes the selected building/student or closes its window.
  const identity = [user?.accountID, user?.userID, user?.userRole].join('|');
  return <SpecialStudentModalSession key={identity} {...props} />;
}

function SpecialStudentModalSession(props) {
  const contextKey = [props.scope?.iAccountID, props.scope?.iCampusID, props.scope?.iBuildingID,
    props.scope?.incidentID, props.student?.studentId, props.student?.iBuildingID, Boolean(props.show)].join('|');
  const [session, setSession] = useState({ key: contextKey, scope: props.scope, student: props.student, show: props.show });
  const guardRef = useRef(null);
  useEffect(() => {
    if (contextKey === session.key) return;
    const apply = () => setSession({ key: contextKey, scope: props.scope, student: props.student, show: props.show });
    if (guardRef.current) guardRef.current(apply);
    else apply();
    // A declined context change keeps the existing record visible until closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);
  return <SpecialStudentModalContent key={session.key} {...props} scope={session.scope} student={session.student} show={session.show} guardRef={guardRef} />;
}

function SpecialStudentModalContent({
  show, onHide, scope, student, onStudentUpdated,
  allowCurrentSpecialEdit = true, readOnly = false, userOverride,
  initialTab = 'current', onDirtyChange, guardRef, requestCloseRef,
}) {
  const { user } = useAccommodationUser(userOverride);
  const effectiveScope = useMemo(() => ({
    ...scope,
    iAccountID: clean(scope?.iAccountID || user?.accountID),
    iCampusID: clean(student?.iCampusID || scope?.iCampusID) || '-99',
    iBuildingID: clean(student?.iBuildingID || scope?.iBuildingID),
    districtSearch: false,
    readOnly: Boolean(readOnly || scope?.readOnly === true || scope?.readOnly === 'true' || scope?.readOnly === 1 || scope?.readOnly === '1'),
  }), [scope, student?.iCampusID, student?.iBuildingID, user?.accountID, readOnly]);

  const [activeTab, setActiveTab] = useState(['current', 'plan', 'adult', 'reviews', 'disclosures'].includes(initialTab) ? initialTab : 'current');
  const [currentEnabled, setCurrentEnabled] = useState(false);
  const [currentNote, setCurrentNote] = useState('');
  const [plan, setPlan] = useState(blankPlan());
  const [adultExtension, setAdultExtension] = useState(blankAdultExtension());
  const [staff, setStaff] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [review, setReview] = useState(blankReview());
  const [correctiveEdit, setCorrectiveEdit] = useState(null);
  const [disclosures, setDisclosures] = useState([]);
  const [disclosure, setDisclosure] = useState(blankDisclosure());
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const historyNeedsRefreshRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [planLoaded, setPlanLoaded] = useState(false);
  const [serverCanEdit, setServerCanEdit] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [remoteChanged, setRemoteChanged] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const revisionRef = useRef(null);
  const activeRef = useRef(true);
  const busyRef = useRef(false);
  const readSequence = useRef(0);
  const dirtyRef = useRef(false);
  const dirtyCallbackRef = useRef(onDirtyChange);
  dirtyCallbackRef.current = onDirtyChange;
  const editorState = { current: { enabled: currentEnabled, note: currentNote }, plan, adult: adultExtension, review, corrective: correctiveEdit, disclosure };
  const dirtyFields = baseline ? Object.keys(editorState).filter((key) => JSON.stringify(editorState[key]) !== JSON.stringify(baseline[key])) : [];
  const dirty = dirtyFields.length > 0;
  dirtyRef.current = dirty;
  const canViewPlan = planLoaded;
  const canEditPlan = planLoaded && serverCanEdit && !effectiveScope.readOnly;
  const canEditCurrent = canEditPlan && allowCurrentSpecialEdit;
  const missingPlanFields = missingPlanRequirements(plan);
  const missingPlanKeys = new Set(missingPlanFields.map(([field]) => field));

  function requiredPlanLabel(field, label) {
    const missing = missingPlanKeys.has(field);
    return <Form.Label htmlFor={`accommodation-plan-${field}`} className={missing ? 'text-danger fw-bold' : undefined}>
      {label}{missing && <span className="small">{field === 'accessNeedsOrInstructions' ? ' — or select an access need above' : ' — required to complete'}</span>}
    </Form.Label>;
  }

  function requiredPlanControl(field) {
    const missing = missingPlanKeys.has(field);
    return { id: `accommodation-plan-${field}`, isInvalid: missing, 'aria-invalid': missing };
  }

  useEffect(() => {
    activeRef.current = true;
    return () => { activeRef.current = false; readSequence.current += 1; };
  }, []);
  useEffect(() => { if (dirtyCallbackRef.current) dirtyCallbackRef.current(dirty); }, [dirty]);
  useEffect(() => () => { if (dirtyCallbackRef.current) dirtyCallbackRef.current(false); }, []);
  useEffect(() => {
    const warn = (event) => { if (dirtyRef.current || busyRef.current) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  function setSavedFields(fields) {
    setBaseline((previous) => ({ ...(previous || {}), ...fields }));
  }

  function clearProtectedData() {
    setPlanLoaded(false); setServerCanEdit(false); setBaseline(null);
    setCurrentEnabled(false); setCurrentNote(''); setPlan(blankPlan());
    setAdultExtension(blankAdultExtension()); setStaff([]); setReviews([]);
    setDisclosures([]); setHistory([]); setHistoryError(''); historyNeedsRefreshRef.current = false; revisionRef.current = null;
  }

  async function loadRecord(background = false) {
    if (!show || !student?.studentId || busyRef.current) return;
    const sequence = ++readSequence.current;
    if (!background) { clearProtectedData(); setLoading(true); setError(''); }
    try {
      const response = await getStudentAccommodation(student.studentId, effectiveScope);
      if (!activeRef.current || sequence !== readSequence.current || busyRef.current) return;
      if (background && dirtyRef.current) {
        if (response.revision !== revisionRef.current) setRemoteChanged(true);
        setServerCanEdit(response.canEdit === true);
        return;
      }
      if (background && response.revision === revisionRef.current && !historyNeedsRefreshRef.current) {
        setServerCanEdit(response.canEdit === true);
        return;
      }
      const [staffResponse, reviewResponse, adultResponse, disclosureResponse, historyResponse] = await Promise.all([
        listAccommodationStaff(effectiveScope),
        listStudentAccommodationReviews(student.studentId, effectiveScope),
        getAdultResidentialExtension(student.studentId, effectiveScope),
        listStudentAccommodationDisclosures(student.studentId, effectiveScope),
        listStudentAccommodationChangeHistory(student.studentId, effectiveScope),
      ]);
      if (!activeRef.current || sequence !== readSequence.current || busyRef.current) return;
      // A keystroke during a background request must never be replaced.
      if (background && dirtyRef.current) { setRemoteChanged(true); return; }
      const latest = await getStudentAccommodation(student.studentId, effectiveScope);
      if (!activeRef.current || sequence !== readSequence.current || busyRef.current) return;
      if (background && dirtyRef.current) { setRemoteChanged(true); return; }
      if (latest.revision !== response.revision) {
        if (background) { setRemoteChanged(true); return; }
        throw new Error('This record changed while loading. Select Retry to load the latest information.');
      }
      const authoritative = response.student || {};
      const fields = {
        current: { enabled: Number(authoritative.specialCircumstance || 0) === 1, note: authoritative.specialCircumstanceNote || '' },
        plan: normalizePlan(response.plan), adult: normalizeAdultExtension(adultResponse.extension),
        review: blankReview(), corrective: null, disclosure: blankDisclosure(),
      };
      setCurrentEnabled(fields.current.enabled); setCurrentNote(fields.current.note);
      setPlan(fields.plan); setAdultExtension(fields.adult); setReview(fields.review);
      setCorrectiveEdit(null); setDisclosure(fields.disclosure); setBaseline(fields);
      setStaff(Array.isArray(staffResponse.rows) ? staffResponse.rows : []);
      setReviews(Array.isArray(reviewResponse.rows) ? reviewResponse.rows : []);
      setDisclosures(Array.isArray(disclosureResponse.rows) ? disclosureResponse.rows : []);
      setHistory(Array.isArray(historyResponse.rows) ? historyResponse.rows : []);
      historyNeedsRefreshRef.current = false; setHistoryError('');
      revisionRef.current = response.revision;
      setServerCanEdit(response.canEdit === true); setPlanLoaded(true);
      setRemoteChanged(false); setError('');
    } catch (loadError) {
      if (!activeRef.current || sequence !== readSequence.current) return;
      const denied = [401, 403].includes(loadError?.response?.status);
      if (!background || denied) clearProtectedData();
      setError(errorText(loadError, 'Unable to load student information. Select Retry.'));
    } finally {
      if (activeRef.current && sequence === readSequence.current) setLoading(false);
    }
  }

  useEffect(() => {
    if (!show) return;
    loadRecord();
    const timer = window.setInterval(() => { if (!document.hidden) loadRecord(true); }, 20000);
    const refresh = () => loadRecord(true);
    window.addEventListener('focus', refresh);
    window.addEventListener('student-accommodations-changed', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); window.removeEventListener('student-accommodations-changed', refresh); readSequence.current += 1; };
    // The wrapper pins student/scope for this editor session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  function publishStudent(response) {
    const nextPlan = response.plan;
    const complete = nextPlan?.planStatus === 'complete';
    const canonical = {
      ...student,
      ...(nextPlan ? {
        accommodationPlanComplete: complete, accommodationPlanStatus: nextPlan.planStatus,
        accommodationReunificationAccommodation: complete ? nextPlan.reunificationAccommodation || null : null,
        accommodationAccessibleTransportNeeded: complete && Boolean(nextPlan.accessibleTransportNeeded),
        accommodationCriticalEquipmentMedication: complete ? nextPlan.criticalEquipmentMedication || null : null,
        accommodationParentGuardianConsideration: complete ? nextPlan.parentGuardianConsideration || null : null,
        accommodationCommunicationMethod: complete ? nextPlan.communicationMethod || null : null,
      } : {}),
      ...(response.extension ? { hasAdultResidential: Boolean(response.extension.adultExtensionId) } : {}),
      ...(response.student || {}), revision: response.revision,
    };
    if (typeof onStudentUpdated === 'function') onStudentUpdated(canonical);
    publishAccommodationChange(effectiveScope, canonical);
  }

  async function mutate(write, apply, message) {
    if (!canEditPlan || busyRef.current || revisionRef.current == null) return false;
    busyRef.current = true; readSequence.current += 1;
    setBusy(true); setError(''); setSuccess('');
    try {
      const response = await write(revisionRef.current);
      if (!activeRef.current) return false;
      revisionRef.current = response.revision;
      apply(response); setRemoteChanged(false); setSuccess(message);
      publishStudent(response);
      try {
        const nextHistory = await listStudentAccommodationChangeHistory(student.studentId, effectiveScope);
        if (activeRef.current) { setHistory(Array.isArray(nextHistory.rows) ? nextHistory.rows : []); setHistoryError(''); historyNeedsRefreshRef.current = false; }
      } catch (_) {
        historyNeedsRefreshRef.current = true;
        if (activeRef.current) setHistoryError('Your changes were saved, but the change history could not refresh.');
      }
      return true;
    } catch (saveError) {
      if (activeRef.current) {
        setError(errorText(saveError, 'Unable to save. Your changes remain here.'));
        if ([409, 428].includes(saveError?.response?.status)) setRemoteChanged(true);
      }
      return false;
    } finally {
      busyRef.current = false;
      if (activeRef.current) setBusy(false);
    }
  }

  function saveCurrentStatus() {
    if (!canEditCurrent) return Promise.resolve(false);
    return mutate((revision) => updateAccommodationSpecialStatus(student.studentId, effectiveScope, {
      specialCircumstance: currentEnabled ? 1 : 0, specialCircumstanceNote: currentNote,
    }, revision), (response) => {
      const current = { enabled: Number(response.student?.specialCircumstance || 0) === 1, note: response.student?.specialCircumstanceNote || '' };
      setCurrentEnabled(current.enabled); setCurrentNote(current.note); setSavedFields({ current });
    }, 'Current Special Status saved.');
  }

  function savePlan(status) {
    return mutate((revision) => saveStudentAccommodation(student.studentId, effectiveScope, { ...plan, planStatus: status }, revision), (response) => {
      const next = normalizePlan(response.plan); setPlan(next); setSavedFields({ plan: next });
    }, status === 'complete' ? 'Emergency accommodation plan marked Complete.' : 'Emergency accommodation plan saved as Draft.');
  }

  function saveAdultExtension() {
    if (!plan.planId) { setError('Save the Emergency Accommodation Plan before adding adult residential information.'); return Promise.resolve(false); }
    return mutate((revision) => saveAdultResidentialExtension(student.studentId, effectiveScope, adultExtension, revision), (response) => {
      const next = normalizeAdultExtension(response.extension); setAdultExtension(next); setSavedFields({ adult: next });
    }, 'Adult Residential Extension saved.');
  }

  function saveReview() {
    return mutate((revision) => addStudentAccommodationReview(student.studentId, effectiveScope, {
      ...review, workedAsIntended: review.workedAsIntended === '' ? null : review.workedAsIntended === 'yes',
    }, revision), (response) => {
      const nextPlan = normalizePlan(response.plan); const nextReview = blankReview();
      setPlan(nextPlan); setReviews(Array.isArray(response.reviews) ? response.reviews : []); setReview(nextReview);
      setSavedFields({ plan: nextPlan, review: nextReview });
    }, 'Plan review recorded.');
  }

  function beginCorrectiveEdit(row) {
    requestTransition(() => {
      const edit = {
        reviewId: row.reviewId, correctiveAction: row.correctiveAction || '',
        correctiveActionOwnerUserID: row.correctiveActionOwnerUserID || '',
        correctiveActionDueDate: row.correctiveActionDueDate || '',
        correctiveActionCompletedAt: row.correctiveActionCompletedAt || '',
        correctiveActionResolution: row.correctiveActionResolution || '',
      };
      setCorrectiveEdit(edit); setSavedFields({ corrective: edit }); setError(''); setSuccess('');
    });
  }

  function saveCorrectiveAction() {
    if (!correctiveEdit?.reviewId) return Promise.resolve(false);
    return mutate((revision) => updateStudentAccommodationReviewCorrectiveAction(student.studentId, correctiveEdit.reviewId, effectiveScope, {
      correctiveActionOwnerUserID: correctiveEdit.correctiveActionOwnerUserID,
      correctiveActionDueDate: correctiveEdit.correctiveActionDueDate,
      correctiveActionCompletedAt: correctiveEdit.correctiveActionCompletedAt,
      correctiveActionResolution: correctiveEdit.correctiveActionResolution,
    }, revision), (response) => {
      const nextPlan = normalizePlan(response.plan); setPlan(nextPlan);
      setReviews(Array.isArray(response.reviews) ? response.reviews : []); setCorrectiveEdit(null);
      setSavedFields({ plan: nextPlan, corrective: null });
    }, 'Corrective action updated.');
  }

  function saveDisclosure() {
    return mutate((revision) => addStudentAccommodationDisclosure(student.studentId, effectiveScope, disclosure, revision), (response) => {
      const next = blankDisclosure(); setDisclosures(Array.isArray(response.rows) ? response.rows : []);
      setDisclosure(next); setSavedFields({ disclosure: next });
    }, 'External emergency disclosure recorded.');
  }

  function resetEdits() {
    if (!baseline) return;
    setCurrentEnabled(baseline.current.enabled); setCurrentNote(baseline.current.note);
    setPlan(baseline.plan); setAdultExtension(baseline.adult); setReview(baseline.review);
    setCorrectiveEdit(baseline.corrective); setDisclosure(baseline.disclosure);
    dirtyRef.current = false;
  }

  function requestTransition(action) {
    if (busyRef.current) return;
    if (dirtyRef.current) setPendingAction(() => action);
    else action();
  }
  const guardedClose = (action = onHide) => requestTransition(action);
  // Current render closures ensure Save uses the latest form values.
  guardRef.current = guardedClose;
  if (requestCloseRef) requestCloseRef.current = guardedClose;

  async function saveAndContinue() {
    const action = pendingAction;
    const saves = { current: saveCurrentStatus, plan: () => savePlan(plan.planStatus || 'draft'), adult: saveAdultExtension, review: saveReview, corrective: saveCorrectiveAction, disclosure: saveDisclosure };
    for (const field of dirtyFields) {
      if (!(await saves[field]())) return;
    }
    dirtyRef.current = false; setPendingAction(null);
    if (action) action();
  }

  async function retryHistory() {
    try {
      const response = await listStudentAccommodationChangeHistory(student.studentId, effectiveScope);
      if (!activeRef.current) return;
      setHistory(Array.isArray(response.rows) ? response.rows : []);
      setHistoryError(''); historyNeedsRefreshRef.current = false;
    } catch (historyLoadError) {
      if (activeRef.current) setHistoryError(errorText(historyLoadError, 'Unable to refresh change history.'));
    }
  }

  function staffName(userID) {
    return staff.find((item) => clean(item.iUserID) === clean(userID))?.name || clean(userID) || '—';
  }

  function updateDailyLocation(index, field, value) {
    setPlan((previous) => ({ ...previous, dailyLocations: previous.dailyLocations.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item) }));
  }
  function updateAdultContact(index, field, value) {
    setAdultExtension((previous) => ({ ...previous, emergencyContacts: previous.emergencyContacts.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item) }));
  }

  const studentName = displayStudentName(student);

  return (
    <>
    <Modal show={show} onHide={busy ? undefined : () => guardedClose()} keyboard={!busy} backdrop={busy ? "static" : true} fullscreen scrollable>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Special Student — {studentName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button size="sm" variant={activeTab === 'current' ? 'warning' : 'outline-warning'} disabled={busy} onClick={() => activeTab !== 'current' && requestTransition(() => setActiveTab('current'))}>
            Current Special Status {currentEnabled ? '●' : ''}
          </Button>
          <Button size="sm" variant={activeTab === 'plan' ? 'primary' : 'outline-primary'} disabled={busy} onClick={() => activeTab !== 'plan' && requestTransition(() => setActiveTab('plan'))}>
            Emergency Accommodation Plan {planLoaded && plan.planStatus === 'complete' ? '●' : ''}
          </Button>
          {canViewPlan && (
            <Button size="sm" variant={activeTab === 'adult' ? 'info' : 'outline-info'} disabled={busy} onClick={() => activeTab !== 'adult' && requestTransition(() => setActiveTab('adult'))}>
              Adult Residential Extension {adultExtension.adultExtensionId ? '●' : ''}
            </Button>
          )}
          {canViewPlan && (
            <Button size="sm" variant={activeTab === 'reviews' ? 'secondary' : 'outline-secondary'} disabled={busy} onClick={() => activeTab !== 'reviews' && requestTransition(() => setActiveTab('reviews'))}>
              Review / Drill History
            </Button>
          )}
          {canViewPlan && (
            <Button size="sm" variant={activeTab === 'disclosures' ? 'dark' : 'outline-dark'} disabled={busy} onClick={() => activeTab !== 'disclosures' && requestTransition(() => setActiveTab('disclosures'))}>
              External Disclosures
            </Button>
          )}
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {planLoaded && !canEditPlan && <Alert variant="info">View only — changes are available to authorized managers.</Alert>}
        {remoteChanged && <Alert variant="warning">This record has newer changes. Your unsaved work has been kept. Reload the latest record before saving again.{' '}<Button size="sm" disabled={busy} onClick={() => requestTransition(() => loadRecord())}>Reload Latest</Button></Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        {historyError && <Alert variant="warning">{historyError}{' '}<Button size="sm" disabled={busy} onClick={retryHistory}>Retry History</Button></Alert>}
        {!loading && !planLoaded && (
          <Alert variant="warning">
            Student information is unavailable. Editing is disabled until it loads successfully.{' '}
            <Button size="sm" onClick={() => loadRecord()}>Retry</Button>
          </Alert>
        )}
        {loading && <div className="py-4 text-center"><Spinner animation="border" size="sm" /> Loading…</div>}

        {!loading && planLoaded && activeTab === 'current' && (
          <>
            <Alert variant="warning">
              Use Current Special Status for a temporary or active situation such as an injury, ambulance/medical event, custody/release concern, or other time-limited safety issue. It is separate from the daily emergency accommodation plan.
            </Alert>
            <Form.Check
              type="switch"
              id="student-current-special-status"
              label={currentEnabled ? 'Current Special Status is ON' : 'Current Special Status is OFF'}
              checked={currentEnabled}
              disabled={!canEditCurrent || busy}
              onChange={(event) => setCurrentEnabled(event.target.checked)}
            />
            <Form.Group className="mt-3">
              <Form.Label>Current status note</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={currentNote}
                disabled={!canEditCurrent || busy || !currentEnabled}
                onChange={(event) => setCurrentNote(event.target.value)}
                placeholder="What does staff need to know about this current situation?"
              />
            </Form.Group>
            {canEditCurrent && (
              <Button className="mt-3" variant="warning" disabled={busy} onClick={saveCurrentStatus}>
                Save Current Special Status
              </Button>
            )}
            <h6 className="mt-4">Special status change history</h6>
            <Table responsive bordered size="sm">
              <thead><tr><th>Changed</th><th>By</th><th>Previous status / note</th><th>Updated status / note</th></tr></thead>
              <tbody>
                {history.filter((row) => ['special-status', 'special_status', 'current'].includes(row.category)).map((row) => (
                  <tr key={row.changeId}>
                    <td>{row.changedAt ? new Date(row.changedAt).toLocaleString() : '—'}</td>
                    <td>{row.changedByName || '—'}</td>
                    <td>{Number(row.before?.specialCircumstance || 0) === 1 ? 'ON' : 'OFF'}<div>{row.before?.specialCircumstanceNote || '—'}</div></td>
                    <td>{Number(row.after?.specialCircumstance || 0) === 1 ? 'ON' : 'OFF'}<div>{row.after?.specialCircumstanceNote || '—'}</div></td>
                  </tr>
                ))}
                {!history.some((row) => ['special-status', 'special_status', 'current'].includes(row.category)) && <tr><td colSpan={4}>No recorded special status changes.</td></tr>}
              </tbody>
            </Table>
          </>
        )}

        {!loading && planLoaded && activeTab === 'plan' && canViewPlan && (
          <fieldset disabled={!canEditPlan || busy}>
            {!plan.planId && <Alert variant="info">No emergency accommodation plan has been saved for this student.</Alert>}
            <div className="d-flex align-items-center gap-2 mb-3">
              <strong>Plan status:</strong>
              <Badge bg={plan.planStatus === 'complete' ? 'primary' : 'secondary'}>
                {plan.planStatus === 'complete' ? 'Complete' : 'Draft'}
              </Badge>
              {!canEditPlan && <span className="text-muted">View only</span>}
            </div>

            {canEditPlan && <Alert variant="info">You can <strong>Save Draft</strong> and return later to finish. Red fields are required before you can mark the plan Complete.</Alert>}
            {missingPlanFields.length > 0 && <Alert variant="danger">
              <strong>Still needed before marking Complete ({missingPlanFields.length}):</strong>
              <ul className="row mb-0 mt-2">{missingPlanFields.map(([field, label]) => <li key={field} className="col-md-6 col-xl-4">{label}</li>)}</ul>
            </Alert>}

            <h5>1. Access & Functional Needs / Required Accommodations</h5>
            <div id="accommodation-plan-types-label" className={`mb-1 ${missingPlanKeys.has('planTypes') ? 'text-danger fw-bold' : ''}`}>Plan type — choose at least one</div>
            <Row className="mb-3" role="group" aria-labelledby="accommodation-plan-types-label" aria-invalid={missingPlanKeys.has('planTypes')}>
              {PLAN_TYPE_OPTIONS.map(([value, label]) => (
                <Col md={4} key={value}>
                  <Form.Check
                    type="checkbox"
                    id={`accommodation-plan-type-${value.replace(/\s+/g, '-')}`}
                    isInvalid={missingPlanKeys.has('planTypes')}
                    label={label}
                    checked={plan.planTypes.includes(value)}
                    onChange={(event) => setPlan((prev) => ({ ...prev, planTypes: toggleArrayValue(prev.planTypes, value, event.target.checked) }))}
                  />
                </Col>
              ))}
            </Row>
            <div id="accommodation-plan-needs-label" className={`mb-1 ${missingPlanKeys.has('accessNeedsOrInstructions') ? 'text-danger fw-bold' : ''}`}>Access / functional needs — choose at least one or enter specific instructions below</div>
            <Row className="mb-3" role="group" aria-labelledby="accommodation-plan-needs-label" aria-invalid={missingPlanKeys.has('accessNeedsOrInstructions')}>
              {NEED_OPTIONS.map(([value, label]) => (
                <Col md={6} key={value} className="mb-1">
                  <Form.Check
                    type="checkbox"
                    id={`accommodation-plan-need-${value}`}
                    isInvalid={missingPlanKeys.has('accessNeedsOrInstructions')}
                    label={label}
                    checked={plan.accessNeeds.includes(value)}
                    onChange={(event) => setPlan((prev) => ({ ...prev, accessNeeds: toggleArrayValue(prev.accessNeeds, value, event.target.checked) }))}
                  />
                </Col>
              ))}
            </Row>
            <Form.Group className="mb-3">
              {requiredPlanLabel('accessNeedsOrInstructions', 'Specific accommodations / instructions')}
              <Form.Control {...requiredPlanControl('accessNeedsOrInstructions')} as="textarea" rows={3} value={plan.specificInstructions} onChange={(e) => setPlan((p) => ({ ...p, specificInstructions: e.target.value }))} />
            </Form.Group>

            <div className="border rounded p-3 mb-4">
              <h6 className="mb-1">National Access & Functional Needs — CMIST</h6>
              <div className="text-muted small mb-2">Functional categories for emergency planning; this does not store a diagnosis.</div>
              <Row>
                {CMIST_OPTIONS.map(([key, label]) => (
                  <Col md={6} lg={4} key={key} className="mb-1">
                    <Form.Check
                      type="checkbox"
                      label={label}
                      checked={Boolean(plan.cmistNeeds?.[key])}
                      onChange={(event) => setPlan((p) => ({ ...p, cmistNeeds: { ...p.cmistNeeds, [key]: event.target.checked } }))}
                    />
                  </Col>
                ))}
              </Row>
            </div>

            <div className="border rounded p-3 mb-4">
              <h6>Accessible emergency communication</h6>
              <Row>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>Preferred method</Form.Label><Form.Control value={plan.emergencyCommunicationNeeds.preferredMethod || ''} onChange={(e) => setPlan((p) => ({ ...p, emergencyCommunicationNeeds: { ...p.emergencyCommunicationNeeds, preferredMethod: e.target.value } }))} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>Preferred language</Form.Label><Form.Control value={plan.emergencyCommunicationNeeds.preferredLanguage || ''} onChange={(e) => setPlan((p) => ({ ...p, emergencyCommunicationNeeds: { ...p.emergencyCommunicationNeeds, preferredLanguage: e.target.value } }))} /></Form.Group></Col>
                {COMMUNICATION_CHECKS.map(([key, label]) => (
                  <Col md={6} key={key} className="mb-1">
                    <Form.Check type="checkbox" label={label} checked={Boolean(plan.emergencyCommunicationNeeds?.[key])} onChange={(e) => setPlan((p) => ({ ...p, emergencyCommunicationNeeds: { ...p.emergencyCommunicationNeeds, [key]: e.target.checked } }))} />
                  </Col>
                ))}
                <Col md={12}><Form.Group className="mt-2 mb-0"><Form.Label>Other communication need</Form.Label><Form.Control value={plan.emergencyCommunicationNeeds.other || ''} onChange={(e) => setPlan((p) => ({ ...p, emergencyCommunicationNeeds: { ...p.emergencyCommunicationNeeds, other: e.target.value } }))} /></Form.Group></Col>
              </Row>
            </div>

            <h5>2. Daily Location & Evacuation</h5>
            <Row>
              <Col md={6}><Form.Group className="mb-3">{requiredPlanLabel('primaryLocation', 'Primary location')}<Form.Control {...requiredPlanControl('primaryLocation')} value={plan.primaryLocation} onChange={(e) => setPlan((p) => ({ ...p, primaryLocation: e.target.value }))} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3">{requiredPlanLabel('refugeSite', 'Pre-evacuation / refuge site')}<Form.Control {...requiredPlanControl('refugeSite')} value={plan.refugeSite} onChange={(e) => setPlan((p) => ({ ...p, refugeSite: e.target.value }))} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3">{requiredPlanLabel('primaryRoute', 'Primary route')}<Form.Control {...requiredPlanControl('primaryRoute')} as="textarea" rows={2} value={plan.primaryRoute} onChange={(e) => setPlan((p) => ({ ...p, primaryRoute: e.target.value }))} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3">{requiredPlanLabel('secondaryRoute', 'Secondary route')}<Form.Control {...requiredPlanControl('secondaryRoute')} as="textarea" rows={2} value={plan.secondaryRoute} onChange={(e) => setPlan((p) => ({ ...p, secondaryRoute: e.target.value }))} /></Form.Group></Col>
            </Row>

            <div className="border rounded p-3 mb-4">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                <div><strong>Additional daily / schedule locations</strong><div className="small text-muted">Optional locations such as classrooms, cafeteria, gym, bus dismissal, or other recurring locations.</div></div>
                {canEditPlan && <Button type="button" size="sm" variant="outline-primary" onClick={() => setPlan((p) => ({ ...p, dailyLocations: [...p.dailyLocations, blankDailyLocation()] }))}>Add Location</Button>}
              </div>
              {plan.dailyLocations.length === 0 ? <div className="text-muted small">No additional locations recorded.</div> : plan.dailyLocations.map((location, index) => (
                <div className="border rounded p-2 mb-2" key={`daily-location-${index}`}>
                  <Row>
                    <Col md={4}><Form.Group className="mb-2"><Form.Label>Location</Form.Label><Form.Control value={location.locationName || ''} onChange={(e) => updateDailyLocation(index, 'locationName', e.target.value)} /></Form.Group></Col>
                    <Col md={4}><Form.Group className="mb-2"><Form.Label>Schedule / time context</Form.Label><Form.Control value={location.scheduleContext || ''} onChange={(e) => updateDailyLocation(index, 'scheduleContext', e.target.value)} /></Form.Group></Col>
                    <Col md={4}><Form.Group className="mb-2"><Form.Label>Refuge site</Form.Label><Form.Control value={location.refugeSite || ''} onChange={(e) => updateDailyLocation(index, 'refugeSite', e.target.value)} /></Form.Group></Col>
                    <Col md={6}><Form.Group className="mb-2"><Form.Label>Primary route</Form.Label><Form.Control value={location.primaryRoute || ''} onChange={(e) => updateDailyLocation(index, 'primaryRoute', e.target.value)} /></Form.Group></Col>
                    <Col md={6}><Form.Group className="mb-2"><Form.Label>Secondary route</Form.Label><Form.Control value={location.secondaryRoute || ''} onChange={(e) => updateDailyLocation(index, 'secondaryRoute', e.target.value)} /></Form.Group></Col>
                  </Row>
                  {canEditPlan && <Button type="button" size="sm" variant="outline-danger" onClick={() => setPlan((p) => ({ ...p, dailyLocations: p.dailyLocations.filter((_, itemIndex) => itemIndex !== index) }))}>Remove</Button>}
                </div>
              ))}
            </div>

            <Row>
              <Col md={6}><Form.Group className="mb-3">{requiredPlanLabel('primaryStaffUserID', 'Primary staff assistant')}<Form.Select {...requiredPlanControl('primaryStaffUserID')} value={plan.primaryStaffUserID} onChange={(e) => setPlan((p) => ({ ...p, primaryStaffUserID: e.target.value }))}><option value="">Select staff…</option>{staff.map((person) => <option key={person.iUserID} value={person.iUserID}>{person.name}{person.positionRole ? ` — ${person.positionRole}` : ''}</option>)}</Form.Select></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3">{requiredPlanLabel('backupStaffUserID', 'Backup staff assistant')}<Form.Select {...requiredPlanControl('backupStaffUserID')} value={plan.backupStaffUserID} onChange={(e) => setPlan((p) => ({ ...p, backupStaffUserID: e.target.value }))}><option value="">Select staff…</option>{staff.map((person) => <option key={person.iUserID} value={person.iUserID}>{person.name}{person.positionRole ? ` — ${person.positionRole}` : ''}</option>)}</Form.Select></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Primary staff briefed / assignment acknowledged</Form.Label><Form.Control type="datetime-local" value={plan.primaryStaffAcknowledgedAt || ''} disabled={!plan.primaryStaffUserID || !canEditPlan || busy} onChange={(e) => setPlan((p) => ({ ...p, primaryStaffAcknowledgedAt: e.target.value }))} /><Form.Text>{plan.primaryStaffAcknowledgedByName ? `Recorded by ${plan.primaryStaffAcknowledgedByName}` : ''}</Form.Text></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Backup staff briefed / assignment acknowledged</Form.Label><Form.Control type="datetime-local" value={plan.backupStaffAcknowledgedAt || ''} disabled={!plan.backupStaffUserID || !canEditPlan || busy} onChange={(e) => setPlan((p) => ({ ...p, backupStaffAcknowledgedAt: e.target.value }))} /><Form.Text>{plan.backupStaffAcknowledgedByName ? `Recorded by ${plan.backupStaffAcknowledgedByName}` : ''}</Form.Text></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Equipment / go-kit location</Form.Label><Form.Control value={plan.equipmentGoKitLocation} onChange={(e) => setPlan((p) => ({ ...p, equipmentGoKitLocation: e.target.value }))} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Medication / device plan</Form.Label><Form.Control as="textarea" rows={2} value={plan.medicationDevicePlan} onChange={(e) => setPlan((p) => ({ ...p, medicationDevicePlan: e.target.value }))} /></Form.Group></Col>
            </Row>

            <div className="border rounded p-3 mb-4">
              <h6>Power-dependent equipment / independence continuity</h6>
              <Row>
                <Col md={4} className="pt-md-4"><Form.Check type="switch" label="Power-dependent equipment" checked={Boolean(plan.powerDependentEquipment.required)} onChange={(e) => setPlan((p) => ({ ...p, powerDependentEquipment: { ...p.powerDependentEquipment, required: e.target.checked } }))} /></Col>
                <Col md={8}><Form.Group className="mb-2"><Form.Label>Equipment description</Form.Label><Form.Control value={plan.powerDependentEquipment.equipmentDescription || ''} onChange={(e) => setPlan((p) => ({ ...p, powerDependentEquipment: { ...p.powerDependentEquipment, equipmentDescription: e.target.value } }))} /></Form.Group></Col>
                <Col md={4}><Form.Check type="switch" label="Backup power required" checked={Boolean(plan.powerDependentEquipment.backupPowerRequired)} onChange={(e) => setPlan((p) => ({ ...p, powerDependentEquipment: { ...p.powerDependentEquipment, backupPowerRequired: e.target.checked } }))} /></Col>
                <Col md={4}><Form.Group className="mb-2"><Form.Label>Battery runtime (minutes)</Form.Label><Form.Control type="number" min="0" value={plan.powerDependentEquipment.batteryRuntimeMinutes ?? ''} onChange={(e) => setPlan((p) => ({ ...p, powerDependentEquipment: { ...p.powerDependentEquipment, batteryRuntimeMinutes: e.target.value } }))} /></Form.Group></Col>
              </Row>
            </div>

            <div className="border rounded p-3 mb-4">
              <Form.Check type="switch" label="Service animal must remain with the student" checked={plan.serviceAnimalRequired} onChange={(e) => setPlan((p) => ({ ...p, serviceAnimalRequired: e.target.checked }))} />
              <Form.Group className="mt-2 mb-0"><Form.Label>Service animal emergency instructions</Form.Label><Form.Control as="textarea" rows={2} value={plan.serviceAnimalInstructions} disabled={!plan.serviceAnimalRequired || !canEditPlan || busy} onChange={(e) => setPlan((p) => ({ ...p, serviceAnimalInstructions: e.target.value }))} /></Form.Group>
            </div>

            <h5>3. Emergency Response Considerations</h5>
            <Form.Group className="mb-3">{requiredPlanLabel('evacuationFireInstructions', 'Evacuation / Fire')}<Form.Control {...requiredPlanControl('evacuationFireInstructions')} as="textarea" rows={2} value={plan.evacuationFireInstructions} onChange={(e) => setPlan((p) => ({ ...p, evacuationFireInstructions: e.target.value }))} /></Form.Group>
            <Form.Group className="mb-3">{requiredPlanLabel('lockdownSecureShelterInstructions', 'Lockdown / Secure / Shelter')}<Form.Control {...requiredPlanControl('lockdownSecureShelterInstructions')} as="textarea" rows={2} value={plan.lockdownSecureShelterInstructions} onChange={(e) => setPlan((p) => ({ ...p, lockdownSecureShelterInstructions: e.target.value }))} /></Form.Group>
            <Form.Group className="mb-4">{requiredPlanLabel('relocationTransportationInstructions', 'Relocation / Transportation')}<Form.Control {...requiredPlanControl('relocationTransportationInstructions')} as="textarea" rows={2} value={plan.relocationTransportationInstructions} onChange={(e) => setPlan((p) => ({ ...p, relocationTransportationInstructions: e.target.value }))} /></Form.Group>

            <h5>4. Reunification / Continuity of Care</h5>
            <Row>
              <Col md={6}><Form.Group className="mb-3">{requiredPlanLabel('reunificationAccommodation', 'Reunification accommodation')}<Form.Control {...requiredPlanControl('reunificationAccommodation')} as="textarea" rows={2} value={plan.reunificationAccommodation} onChange={(e) => setPlan((p) => ({ ...p, reunificationAccommodation: e.target.value }))} /></Form.Group></Col>
              <Col md={6} className="pt-md-4"><Form.Check type="switch" label="Accessible transport needed" checked={plan.accessibleTransportNeeded} onChange={(e) => setPlan((p) => ({ ...p, accessibleTransportNeeded: e.target.checked }))} /></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Staff remaining with student</Form.Label><Form.Control value={plan.staffRemainingWithStudent} onChange={(e) => setPlan((p) => ({ ...p, staffRemainingWithStudent: e.target.value }))} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Critical equipment / medication</Form.Label><Form.Control as="textarea" rows={2} value={plan.criticalEquipmentMedication} onChange={(e) => setPlan((p) => ({ ...p, criticalEquipmentMedication: e.target.value }))} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Parent / guardian consideration</Form.Label><Form.Control as="textarea" rows={2} value={plan.parentGuardianConsideration} onChange={(e) => setPlan((p) => ({ ...p, parentGuardianConsideration: e.target.value }))} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3">{requiredPlanLabel('communicationMethod', 'Communication method / staff instructions')}<Form.Control {...requiredPlanControl('communicationMethod')} as="textarea" rows={2} value={plan.communicationMethod} onChange={(e) => setPlan((p) => ({ ...p, communicationMethod: e.target.value }))} /></Form.Group></Col>
            </Row>

            <h5>5. Review / Plan Validation</h5>
            <Row>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Last drill tested</Form.Label><Form.Control type="date" value={plan.lastDrillTested || ''} onChange={(e) => setPlan((p) => ({ ...p, lastDrillTested: e.target.value }))} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Plan worked as intended</Form.Label><Form.Select value={plan.planWorkedAsIntended === null ? '' : plan.planWorkedAsIntended ? 'yes' : 'no'} onChange={(e) => setPlan((p) => ({ ...p, planWorkedAsIntended: e.target.value === '' ? null : e.target.value === 'yes' }))}><option value="">Not recorded</option><option value="yes">Yes</option><option value="no">No</option></Form.Select></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Next review date</Form.Label><Form.Control type="date" value={plan.nextReviewDate || ''} onChange={(e) => setPlan((p) => ({ ...p, nextReviewDate: e.target.value }))} /></Form.Group></Col>
              <Col md={12}><Form.Group className="mb-3"><Form.Label>Changes needed</Form.Label><Form.Control as="textarea" rows={2} value={plan.changesNeeded} onChange={(e) => setPlan((p) => ({ ...p, changesNeeded: e.target.value }))} /></Form.Group></Col>
            </Row>

          </fieldset>
        )}

        {!loading && planLoaded && activeTab === 'adult' && canViewPlan && (
          <>
            <Alert variant="info">
              Optional Adult Residential Care occupant extension. It keeps adult evacuation, ADL, supervision, contact, and relocation-continuity data separate from the K-12 plan and does not affect the K-12 Draft/Complete status.
            </Alert>
            {!plan?.planId && <Alert variant="warning">Save the Emergency Accommodation Plan first. The adult extension attaches to that plan.</Alert>}
            <fieldset disabled={!canEditPlan || busy || !plan?.planId}>
              <h5>Evacuation & Transfer Capability</h5>
              <Row>
                <Col md={4}><Form.Group className="mb-3"><Form.Label>Can self-evacuate?</Form.Label><Form.Select value={adultExtension.canSelfEvacuate === null ? '' : adultExtension.canSelfEvacuate ? 'yes' : 'no'} onChange={(e) => setAdultExtension((a) => ({ ...a, canSelfEvacuate: e.target.value === '' ? null : e.target.value === 'yes' }))}><option value="">Not recorded</option><option value="yes">Yes</option><option value="no">No</option></Form.Select></Form.Group></Col>
                <Col md={4}><Form.Group className="mb-3"><Form.Label>Evacuation assistance level</Form.Label><Form.Select value={adultExtension.evacuationAssistanceLevel || ''} onChange={(e) => setAdultExtension((a) => ({ ...a, evacuationAssistanceLevel: e.target.value }))}><option value="">Select…</option><option value="independent">Independent</option><option value="verbal_cueing">Verbal cueing</option><option value="one_person_assist">One-person assist</option><option value="two_person_assist">Two-person assist</option><option value="wheelchair_assist">Wheelchair assist</option><option value="mechanical_lift">Mechanical lift</option><option value="stair_device">Stair device</option><option value="stretcher">Stretcher</option><option value="other">Other</option></Form.Select></Form.Group></Col>
                <Col md={4}><Form.Group className="mb-3"><Form.Label>Mobility device</Form.Label><Form.Control value={adultExtension.mobilityDevice || ''} onChange={(e) => setAdultExtension((a) => ({ ...a, mobilityDevice: e.target.value }))} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>Stair capability</Form.Label><Form.Control value={adultExtension.stairCapability || ''} onChange={(e) => setAdultExtension((a) => ({ ...a, stairCapability: e.target.value }))} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>Transfer instructions</Form.Label><Form.Control as="textarea" rows={2} value={adultExtension.transferInstructions || ''} onChange={(e) => setAdultExtension((a) => ({ ...a, transferInstructions: e.target.value }))} /></Form.Group></Col>
              </Row>

              <h5>Emergency ADL / Health Continuity</h5>
              <Row className="mb-2">
                {ADL_OPTIONS.map(([key, label]) => (
                  <Col md={4} key={key} className="mb-1"><Form.Check type="checkbox" label={label} checked={Boolean(adultExtension.emergencyADLNeeds?.[key])} onChange={(e) => setAdultExtension((a) => ({ ...a, emergencyADLNeeds: { ...a.emergencyADLNeeds, [key]: e.target.checked } }))} /></Col>
                ))}
              </Row>
              <Row>
                <Col md={4}><Form.Check className="mb-3" type="switch" label="Oxygen required" checked={adultExtension.oxygenRequired} onChange={(e) => setAdultExtension((a) => ({ ...a, oxygenRequired: e.target.checked }))} /></Col>
                <Col md={4}><Form.Check className="mb-3" type="switch" label="Refrigerated medication required" checked={adultExtension.refrigeratedMedicationRequired} onChange={(e) => setAdultExtension((a) => ({ ...a, refrigeratedMedicationRequired: e.target.checked }))} /></Col>
                <Col md={12}><Form.Group className="mb-3"><Form.Label>Essential personal items / supports</Form.Label><Form.Control as="textarea" rows={2} value={adultExtension.essentialPersonalItems || ''} onChange={(e) => setAdultExtension((a) => ({ ...a, essentialPersonalItems: e.target.value }))} /></Form.Group></Col>
              </Row>

              <h5>Supervision & Safety</h5>
              <Row>
                <Col md={4}><Form.Group className="mb-3"><Form.Label>Supervision level</Form.Label><Form.Select value={adultExtension.supervisionLevel || ''} onChange={(e) => setAdultExtension((a) => ({ ...a, supervisionLevel: e.target.value }))}><option value="">Not recorded</option><option value="independent">Independent</option><option value="periodic">Periodic</option><option value="continuous">Continuous</option><option value="one_to_one">One-to-one</option></Form.Select></Form.Group></Col>
                <Col md={4} className="pt-md-4"><Form.Check type="switch" label="Elopement risk" checked={adultExtension.elopementRisk} onChange={(e) => setAdultExtension((a) => ({ ...a, elopementRisk: e.target.checked }))} /></Col>
                <Col md={12}><Form.Group className="mb-3"><Form.Label>Supervision instructions</Form.Label><Form.Control as="textarea" rows={2} value={adultExtension.supervisionInstructions || ''} onChange={(e) => setAdultExtension((a) => ({ ...a, supervisionInstructions: e.target.value }))} /></Form.Group></Col>
              </Row>

              <h5>Emergency Contacts / Representative</h5>
              <div className="mb-3">
                {adultExtension.emergencyContacts.length === 0 ? <div className="text-muted small mb-2">No adult-residential emergency contacts recorded.</div> : adultExtension.emergencyContacts.map((contact, index) => (
                  <div className="border rounded p-2 mb-2" key={`adult-contact-${index}`}>
                    <Row>
                      <Col md={3}><Form.Group className="mb-2"><Form.Label>Relationship / role</Form.Label><Form.Control value={contact.relationship || ''} onChange={(e) => updateAdultContact(index, 'relationship', e.target.value)} /></Form.Group></Col>
                      <Col md={3}><Form.Group className="mb-2"><Form.Label>Name</Form.Label><Form.Control value={contact.name || ''} onChange={(e) => updateAdultContact(index, 'name', e.target.value)} /></Form.Group></Col>
                      <Col md={3}><Form.Group className="mb-2"><Form.Label>Phone</Form.Label><Form.Control value={contact.phone || ''} onChange={(e) => updateAdultContact(index, 'phone', e.target.value)} /></Form.Group></Col>
                      <Col md={3}><Form.Group className="mb-2"><Form.Label>Email</Form.Label><Form.Control value={contact.email || ''} onChange={(e) => updateAdultContact(index, 'email', e.target.value)} /></Form.Group></Col>
                      <Col md={12}><Form.Group className="mb-2"><Form.Label>Emergency contact instructions</Form.Label><Form.Control value={contact.instructions || ''} onChange={(e) => updateAdultContact(index, 'instructions', e.target.value)} /></Form.Group></Col>
                    </Row>
                    {canEditPlan && <Button type="button" size="sm" variant="outline-danger" onClick={() => setAdultExtension((a) => ({ ...a, emergencyContacts: a.emergencyContacts.filter((_, itemIndex) => itemIndex !== index) }))}>Remove Contact</Button>}
                  </div>
                ))}
                {canEditPlan && <Button type="button" size="sm" variant="outline-primary" onClick={() => setAdultExtension((a) => ({ ...a, emergencyContacts: [...a.emergencyContacts, blankEmergencyContact()] }))}>Add Contact</Button>}
              </div>

              <h5>Relocation Continuity</h5>
              <Row className="mb-3">
                <Col md={6}><Form.Check type="switch" label="Staff must accompany occupant" checked={adultExtension.staffMustAccompany} onChange={(e) => setAdultExtension((a) => ({ ...a, staffMustAccompany: e.target.checked }))} /></Col>
                <Col md={6}><Form.Check type="switch" label="Equipment must accompany occupant" checked={adultExtension.equipmentMustAccompany} onChange={(e) => setAdultExtension((a) => ({ ...a, equipmentMustAccompany: e.target.checked }))} /></Col>
                <Col md={6}><Form.Check type="switch" label="Medications must accompany occupant" checked={adultExtension.medicationsMustAccompany} onChange={(e) => setAdultExtension((a) => ({ ...a, medicationsMustAccompany: e.target.checked }))} /></Col>
                <Col md={6}><Form.Check type="switch" label="Essential records must accompany occupant" checked={adultExtension.recordsMustAccompany} onChange={(e) => setAdultExtension((a) => ({ ...a, recordsMustAccompany: e.target.checked }))} /></Col>
              </Row>

              {canEditPlan && <Button variant="info" disabled={busy || !plan?.planId} onClick={saveAdultExtension}>Save Adult Residential Extension</Button>}
            </fieldset>
          </>
        )}

        {!loading && planLoaded && activeTab === 'reviews' && canViewPlan && (
          <>
            {canEditPlan && (
              <fieldset disabled={busy} className="border rounded p-3 mb-4">
                <h5>Record drill / incident review</h5>
                <Row>
                  <Col md={3}><Form.Group className="mb-3"><Form.Label>Date</Form.Label><Form.Control type="date" value={review.eventDate} onChange={(e) => setReview((r) => ({ ...r, eventDate: e.target.value }))} /></Form.Group></Col>
                  <Col md={3}><Form.Group className="mb-3"><Form.Label>Event</Form.Label><Form.Select value={review.eventType} onChange={(e) => setReview((r) => ({ ...r, eventType: e.target.value }))}><option value="drill">Drill</option><option value="incident">Incident</option><option value="evacuation">Evacuation</option><option value="reunification">Reunification</option><option value="other">Other</option></Form.Select></Form.Group></Col>
                  <Col md={3}><Form.Group className="mb-3"><Form.Label>Worked as intended?</Form.Label><Form.Select value={review.workedAsIntended} onChange={(e) => setReview((r) => ({ ...r, workedAsIntended: e.target.value }))}><option value="">Not recorded</option><option value="yes">Yes</option><option value="no">No</option></Form.Select></Form.Group></Col>
                  <Col md={3}><Form.Group className="mb-3"><Form.Label>Next review</Form.Label><Form.Control type="date" value={review.nextReviewDate} onChange={(e) => setReview((r) => ({ ...r, nextReviewDate: e.target.value }))} /></Form.Group></Col>
                  <Col md={6}><Form.Group className="mb-3"><Form.Label>Problem observed</Form.Label><Form.Control as="textarea" rows={2} value={review.problemObserved} onChange={(e) => setReview((r) => ({ ...r, problemObserved: e.target.value }))} /></Form.Group></Col>
                  <Col md={6}><Form.Group className="mb-3"><Form.Label>Corrective action</Form.Label><Form.Control as="textarea" rows={2} value={review.correctiveAction} onChange={(e) => setReview((r) => ({ ...r, correctiveAction: e.target.value }))} /></Form.Group></Col>
                  <Col md={4}><Form.Group className="mb-3"><Form.Label>Corrective-action owner</Form.Label><Form.Select value={review.correctiveActionOwnerUserID} onChange={(e) => setReview((r) => ({ ...r, correctiveActionOwnerUserID: e.target.value }))}><option value="">Not assigned</option>{staff.map((person) => <option key={person.iUserID} value={person.iUserID}>{person.name}{person.positionRole ? ` — ${person.positionRole}` : ''}</option>)}</Form.Select></Form.Group></Col>
                  <Col md={4}><Form.Group className="mb-3"><Form.Label>Corrective-action due date</Form.Label><Form.Control type="date" value={review.correctiveActionDueDate} onChange={(e) => setReview((r) => ({ ...r, correctiveActionDueDate: e.target.value }))} /></Form.Group></Col>
                  <Col md={4}><Form.Group className="mb-3"><Form.Label>Corrective action completed</Form.Label><Form.Control type="datetime-local" value={review.correctiveActionCompletedAt} onChange={(e) => setReview((r) => ({ ...r, correctiveActionCompletedAt: e.target.value }))} /></Form.Group></Col>
                  <Col md={12}><Form.Group className="mb-3"><Form.Label>Corrective-action resolution</Form.Label><Form.Control as="textarea" rows={2} value={review.correctiveActionResolution} onChange={(e) => setReview((r) => ({ ...r, correctiveActionResolution: e.target.value }))} /></Form.Group></Col>
                </Row>
                <Button disabled={busy || !review.eventDate} onClick={saveReview}>Record Review</Button>
              </fieldset>
            )}

            <Table responsive bordered hover size="sm">
              <thead><tr><th>Date</th><th>Event</th><th>Worked</th><th>Problem</th><th>Corrective action / owner</th><th>Due / complete</th><th>Next review</th><th>Reviewed by</th>{canEditPlan && <th></th>}</tr></thead>
              <tbody>
                {reviews.length === 0 ? (
                  <tr><td colSpan={canEditPlan ? 9 : 8} className="text-center text-muted">No review history yet.</td></tr>
                ) : reviews.map((row) => (
                  <tr key={row.reviewId}>
                    <td>{row.eventDate || '—'}</td>
                    <td>{row.eventType || '—'}</td>
                    <td>{row.workedAsIntended === null ? '—' : row.workedAsIntended ? 'Yes' : 'No'}</td>
                    <td>{row.problemObserved || '—'}</td>
                    <td><div>{row.correctiveAction || '—'}</div>{row.correctiveActionOwnerUserID && <div className="small text-muted">Owner: {staffName(row.correctiveActionOwnerUserID)}</div>}{row.correctiveActionResolution && <div className="small text-muted">Resolution: {row.correctiveActionResolution}</div>}</td>
                    <td><div>Due: {row.correctiveActionDueDate || '—'}</div><div className="small text-muted">Completed: {row.correctiveActionCompletedAt || '—'}</div></td>
                    <td>{row.nextReviewDate || '—'}</td>
                    <td>{row.reviewedByName || row.reviewedByUserID || '—'}</td>
                    {canEditPlan && <td>{row.correctiveAction ? <Button size="sm" variant="outline-secondary" onClick={() => beginCorrectiveEdit(row)}>Update action</Button> : '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </Table>

            {canEditPlan && correctiveEdit && (
              <fieldset disabled={busy} className="border rounded p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                  <h6 className="mb-0">Corrective-action follow-up</h6>
                  <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => requestTransition(() => { setCorrectiveEdit(null); setSavedFields({ corrective: null }); })}>Cancel</Button>
                </div>
                <div className="small text-muted mb-3">{correctiveEdit.correctiveAction}</div>
                <Row>
                  <Col md={4}><Form.Group className="mb-3"><Form.Label>Owner</Form.Label><Form.Select value={correctiveEdit.correctiveActionOwnerUserID} onChange={(e) => setCorrectiveEdit((current) => ({ ...current, correctiveActionOwnerUserID: e.target.value }))}><option value="">Not assigned</option>{staff.map((person) => <option key={person.iUserID} value={person.iUserID}>{person.name}{person.positionRole ? ` — ${person.positionRole}` : ''}</option>)}</Form.Select></Form.Group></Col>
                  <Col md={4}><Form.Group className="mb-3"><Form.Label>Due date</Form.Label><Form.Control type="date" value={correctiveEdit.correctiveActionDueDate} onChange={(e) => setCorrectiveEdit((current) => ({ ...current, correctiveActionDueDate: e.target.value }))} /></Form.Group></Col>
                  <Col md={4}><Form.Group className="mb-3"><Form.Label>Completed</Form.Label><div className="d-flex gap-2"><Form.Control type="datetime-local" value={correctiveEdit.correctiveActionCompletedAt} onChange={(e) => setCorrectiveEdit((current) => ({ ...current, correctiveActionCompletedAt: e.target.value }))} /><Button type="button" size="sm" variant="outline-success" onClick={() => setCorrectiveEdit((current) => ({ ...current, correctiveActionCompletedAt: localDateTimeInput() }))}>Now</Button></div></Form.Group></Col>
                  <Col md={12}><Form.Group className="mb-3"><Form.Label>Resolution</Form.Label><Form.Control as="textarea" rows={2} value={correctiveEdit.correctiveActionResolution} onChange={(e) => setCorrectiveEdit((current) => ({ ...current, correctiveActionResolution: e.target.value }))} placeholder="Required when the corrective action is marked complete." /></Form.Group></Col>
                </Row>
                <Button disabled={busy || (Boolean(correctiveEdit.correctiveActionCompletedAt) && !correctiveEdit.correctiveActionResolution.trim())} onClick={saveCorrectiveAction}>Save Corrective Action</Button>
              </fieldset>
            )}
          </>
        )}

        {!loading && planLoaded && activeTab === 'disclosures' && canViewPlan && (
          <>
            <Alert variant="secondary">
              Append-only log for external emergency disclosure of personally identifiable accommodation information. Record the significant threat/basis and the parties who received the information. This is not a general staff-access log.
            </Alert>
            {!plan?.planId && <Alert variant="warning">Save the Emergency Accommodation Plan before recording an external emergency disclosure.</Alert>}
            {canEditPlan && plan?.planId && (
              <fieldset disabled={busy} className="border rounded p-3 mb-4">
                <h5>Record external emergency disclosure</h5>
                <Row>
                  <Col md={4}><Form.Group className="mb-3"><Form.Label>Disclosure date/time</Form.Label><Form.Control type="datetime-local" value={disclosure.disclosedAt} onChange={(e) => setDisclosure((d) => ({ ...d, disclosedAt: e.target.value }))} /></Form.Group></Col>
                  <Col md={4}><Form.Group className="mb-3"><Form.Label>Incident / reference</Form.Label><Form.Control value={disclosure.incidentReference} onChange={(e) => setDisclosure((d) => ({ ...d, incidentReference: e.target.value }))} /></Form.Group></Col>
                  <Col md={4}><Form.Group className="mb-3"><Form.Label>Disclosure basis</Form.Label><Form.Control value={disclosure.disclosureBasis} onChange={(e) => setDisclosure((d) => ({ ...d, disclosureBasis: e.target.value }))} /></Form.Group></Col>
                  <Col md={6}><Form.Group className="mb-3"><Form.Label>Significant threat / emergency basis</Form.Label><Form.Control as="textarea" rows={2} value={disclosure.significantThreat} onChange={(e) => setDisclosure((d) => ({ ...d, significantThreat: e.target.value }))} /></Form.Group></Col>
                  <Col md={6}><Form.Group className="mb-3"><Form.Label>Recipient party / parties</Form.Label><Form.Control as="textarea" rows={2} value={disclosure.recipientParties} onChange={(e) => setDisclosure((d) => ({ ...d, recipientParties: e.target.value }))} /></Form.Group></Col>
                  <Col md={12}><Form.Group className="mb-3"><Form.Label>Plan information disclosed <span className="text-danger">*</span></Form.Label><Form.Control required as="textarea" rows={2} value={disclosure.fieldsDisclosed} onChange={(e) => setDisclosure((d) => ({ ...d, fieldsDisclosed: e.target.value }))} placeholder="Describe the minimum information actually shared." /></Form.Group></Col>
                </Row>
                <Button variant="dark" disabled={busy || !disclosure.significantThreat.trim() || !disclosure.recipientParties.trim() || !disclosure.disclosureBasis.trim() || !disclosure.fieldsDisclosed.trim()} onClick={saveDisclosure}>Record Disclosure</Button>
              </fieldset>
            )}

            <Table responsive bordered hover size="sm">
              <thead><tr><th>Date/time</th><th>Threat / basis</th><th>Recipients</th><th>Information disclosed</th><th>Reference</th><th>Recorded by</th></tr></thead>
              <tbody>
                {disclosures.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-muted">No external emergency disclosures recorded.</td></tr>
                ) : disclosures.map((row) => (
                  <tr key={row.disclosureId}>
                    <td>{row.disclosedAt || '—'}</td>
                    <td><div>{row.significantThreat || '—'}</div><div className="small text-muted">{row.disclosureBasis || '—'}</div></td>
                    <td>{row.recipientParties || '—'}</td>
                    <td>{row.fieldsDisclosed || '—'}</td>
                    <td>{row.incidentReference || '—'}</td>
                    <td>{row.disclosedByName || row.disclosedByUserID || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!loading && planLoaded && activeTab === 'plan' && canEditPlan && <>
          <div className="me-auto small text-muted">Save a draft now and come back later to finish.</div>
          <Button type="button" variant="outline-secondary" disabled={busy} onClick={() => savePlan('draft')}>Save Draft</Button>
          <Button type="button" variant="primary" disabled={busy} onClick={() => savePlan('complete')}>Save & Mark Complete</Button>
        </>}
        <Button variant="secondary" disabled={busy} onClick={() => guardedClose()}>Close</Button>
      </Modal.Footer>
    </Modal>
    <Modal show={Boolean(pendingAction)} onHide={() => !busy && setPendingAction(null)} centered backdrop="static" keyboard={!busy}>
      <Modal.Header><Modal.Title>Unsaved changes</Modal.Title></Modal.Header>
      <Modal.Body>
        Save changes to {studentName} before continuing?
        {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={() => setPendingAction(null)}>Keep Editing</Button>
        <Button variant="outline-danger" disabled={busy} onClick={() => { const action = pendingAction; resetEdits(); setPendingAction(null); if (action) action(); }}>Discard</Button>
        <Button disabled={busy || !canEditPlan || remoteChanged} onClick={saveAndContinue}>Save</Button>
      </Modal.Footer>
    </Modal>
    </>
  );
}
